import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Avatar,
  Stack,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  PhotoCamera as PhotoCameraIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { compressImage } from '../../utils/imageCompression';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const ImagePreview = styled(Box)(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.spacing(1),
  overflow: 'hidden',
  border: `2px dashed ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.grey[50],
  '&:hover': {
    backgroundColor: theme.palette.grey[100],
  },
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1),
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  color: 'white',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
  },
}));

interface ImageUploadProps {
  images: (string | null)[];
  maxImages: number;
  onImagesChange: (images: (string | null)[]) => void;
  loading?: boolean;
  variant?: 'profile' | 'event';
  size?: number;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  images,
  maxImages,
  onImagesChange,
  loading = false,
  variant = 'event',
  size = 200
}) => {
  const [error, setError] = useState<string | null>(null);

  const validateAndProcessFile = async (file: File): Promise<string | null> => {
    try {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return null;
      }

      // Compress image to meet size requirements
      const maxSizeMB = variant === 'profile' ? 1.5 : 4; // Target smaller than limit for safety
      const maxDimension = variant === 'profile' ? 800 : 1920; // Profile pics don't need to be huge
      
      const compressedImage = await compressImage(file, {
        maxSizeMB,
        maxWidthOrHeight: maxDimension,
        quality: 0.85
      });

      return compressedImage;
    } catch (error) {
      console.error('Image compression error:', error);
      setError('Failed to process image. Please try a smaller file.');
      return null;
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setError(null);

    const file = files[0];
    const base64Image = await validateAndProcessFile(file);
    
    if (base64Image) {
      // Find first empty slot or replace first image for profile
      const newImages = [...images];
      const emptyIndex = newImages.findIndex(img => img === null);
      
      if (variant === 'profile') {
        // For profile, always replace the first (and only) image
        newImages[0] = base64Image;
      } else {
        // For events, add to first empty slot
        if (emptyIndex !== -1) {
          newImages[emptyIndex] = base64Image;
        } else if (newImages.length < maxImages) {
          newImages.push(base64Image);
        } else {
          setError(`Maximum ${maxImages} images allowed`);
          return;
        }
      }
      
      onImagesChange(newImages);
    }

    // Reset input
    event.target.value = '';
  };

  const handleRemoveImage = (index: number) => {
    const newImages = [...images];
    newImages[index] = null;
    onImagesChange(newImages);
  };

  const hasEmptySlot = images.some(img => img === null) || images.length < maxImages;

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack spacing={2}>
        {variant === 'profile' ? (
          // Profile picture upload (single image)
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={images[0] || undefined}
              sx={{ width: size, height: size }}
            >
              <PhotoCameraIcon sx={{ fontSize: size / 3 }} />
            </Avatar>
            
            <Stack spacing={1}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                disabled={loading}
              >
                {images[0] ? 'Change Picture' : 'Upload Picture'}
                <VisuallyHiddenInput
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                />
              </Button>
              
              {images[0] && (
                <Button
                  variant="text"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => handleRemoveImage(0)}
                  disabled={loading}
                  size="small"
                >
                  Remove
                </Button>
              )}
            </Stack>
          </Box>
        ) : (
          // Event images upload (multiple images)
          <Box>
            <Typography variant="h6" gutterBottom>
              Event Images (Max {maxImages})
            </Typography>
            
            <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 2 }}>
              {/* Existing images */}
              {images.map((image, index) => (
                image && (
                  <ImagePreview
                    key={index}
                    sx={{ width: size, height: size, position: 'relative' }}
                  >
                    <img
                      src={image}
                      alt={`Event ${index + 1}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    <DeleteButton
                      size="small"
                      onClick={() => handleRemoveImage(index)}
                      disabled={loading}
                    >
                      <DeleteIcon fontSize="small" />
                    </DeleteButton>
                  </ImagePreview>
                )
              ))}
              
              {/* Add new image button */}
              {hasEmptySlot && (
                <ImagePreview
                  sx={{ 
                    width: size, 
                    height: size,
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: 'primary.main',
                    }
                  }}
                  component="label"
                >
                  {loading ? (
                    <CircularProgress />
                  ) : (
                    <Stack alignItems="center" spacing={1}>
                      <CloudUploadIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        Add Image
                      </Typography>
                    </Stack>
                  )}
                  <VisuallyHiddenInput
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    disabled={loading}
                  />
                </ImagePreview>
              )}
            </Stack>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default ImageUpload;
